import { useMemo, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";
import { toast } from "sonner";
import jsPDF from "jspdf";
import { applyPlugin } from "jspdf-autotable";
applyPlugin(jsPDF);

export const FSISanctionCosts = ({ formData, setFormData, fsiBreakdown, setFSICosts, appConfig = null }) => {

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // Calculate building age from completion year
  const getBuildingAge = () => {
    if (!formData.completionYear) return 0;
    const completionYear = parseInt(formData.completionYear);
    const currentYear = new Date().getFullYear();
    return currentYear - completionYear;
  };

  const costBreakdown = useMemo(() => {
    if (!fsiBreakdown || !formData.rrRatePlot || !formData.rrRateConstruction) {
      return null;
    }

    // Apply RR Multiplier (default 5%)
    const rrMultiplierPercent = parseFloat(formData.rrMultiplier) || 5;
    const rrMultiplierFactor = 1 + (rrMultiplierPercent / 100);
    
    // Apply additional RR markup for TDR/FSI costs if enabled
    const rrAdditionalMarkup = formData.applyRRMarkup === "yes" ? (parseFloat(formData.rrAdditionalMarkupPercent) || 0) : 0;
    const rrMarkupFactor = 1 + (rrAdditionalMarkup / 100);
    
    const rrRatePlot = (parseFloat(formData.rrRatePlot) || 0) * rrMultiplierFactor * rrMarkupFactor;
    const rrRateConstruction = (parseFloat(formData.rrRateConstruction) || 0) * rrMultiplierFactor * rrMarkupFactor;
    const buildingAge = getBuildingAge();
    const isCommercial = formData.projectType === "commercial";
    const commercialPercentage = parseFloat(formData.commercialPercentage) || 0;
    // Use projectClassification instead of isMhadaProject
    const isMhada = formData.projectClassification === "mhada";
    const numMembers = parseInt(formData.numMembers) || 0;
    const mhadaNumBuildings = parseInt(formData.mhadaNumBuildings) || 1;

    // Plot area in sq mtr
    const plotAreaSqMtr = formData.plotAreaUnit === "sqft" ? 
      (parseFloat(formData.plotAreaPerCard) || 0) * 0.092903 : 
      (parseFloat(formData.plotAreaPerCard) || 0);

    // ============ MHADA SPECIFIC CALCULATIONS ============
    if (isMhada) {
      // Titbit Area
      const titbitArea = (formData.hasTitbitArea === "yes") ? (parseFloat(formData.titbitArea) || 0) : 0;
      const existingBuiltupArea = parseFloat(formData.existingBuiltupArea) || 0;

      // MHADA FSI Purchase Price:
      // Payable FSI Area = (Plot Area + Titbit Area) × 3 - (Existing Built-up Area + (Titbit Area × 1.1))
      const baseFSIArea = (plotAreaSqMtr + titbitArea) * 3;
      const deduction = existingBuiltupArea + (titbitArea * 1.1);
      const mhadaFSIArea = baseFSIArea - deduction;

      // MHADA Premium Cost = Payable FSI Area × RR Rate (Land) × 45%
      const mhadaFSICost = mhadaFSIArea * rrRatePlot * 0.45;

      // Titbit Area Cost = 50% of Land RR × titbit area
      const titbitAreaCost = titbitArea * rrRatePlot * 0.50;

      // Pro-rata FSI = 26.2 sq mtr per tenement (free, additional)
      const proRataFSI = numMembers * 26.2;

      // Ancillary FSI remains same
      const ancillaryFSICost = fsiBreakdown.totalAncillary * rrRatePlot * 0.15;

      // MHADA Specific Costs
      const mhadaScrutiny = 27000 * mhadaNumBuildings;
      const mhadaDebris = 6600 * mhadaNumBuildings;
      const mhadaLayout = 1000 * numMembers;
      const mhadaWaterDeposit = 100000 * mhadaNumBuildings;

      // Infrastructure = 7% × Land RR × Total FSI × 50%
      const mhadaPermissibleFSI = fsiBreakdown?.effectiveFSI || 3.0;
      const totalFSIForInfra = plotAreaSqMtr * mhadaPermissibleFSI + proRataFSI + fsiBreakdown.totalAncillary;
      const mhadaInfrastructure = totalFSIForInfra * rrRatePlot * 0.07 * 0.50;
      const mhadaPMSInfra = mhadaInfrastructure; // Same amount

      // Sanction Costs = Plot Area × 2 × Rate per sft
      const mhadaSanctionRate = parseFloat(formData.mhadaSanctionRate) || 300;
      const plotAreaSqft = plotAreaSqMtr / 0.092903;
      const mhadaSanctionCost = plotAreaSqft * 2 * mhadaSanctionRate;

      // Other standard charges still apply
      const scrutinyCharges = totalFSIForInfra * 5;
      const radaRodaCharges = totalFSIForInfra * 20;
      const demolitionCharges = formData.projectMode === "greenfield" ? 0 : (existingBuiltupArea * 50 / 1000 * 227);
      const waterLineCharges = Math.sqrt(plotAreaSqMtr) * 3640;
      const cdCharges = totalFSIForInfra / 1000 * 227;

      // Non-FSI Area
      const fourWheelerPerMember = parseFloat(formData.fourWheelerPerMember) || 1;
      const twoWheelerPerMember = parseFloat(formData.twoWheelerPerMember) || 1;
      const totalCarParking = numMembers * fourWheelerPerMember;
      const total2WheelerParking = numMembers * twoWheelerPerMember;
      const nonFSIArea = (totalCarParking * 20) + (total2WheelerParking * 4);
      const nonFSICharges = nonFSIArea * 0.01 * rrRateConstruction;

      // ULC
      let ulcCharges = 0;
      let ulcArea = 0;
      if (formData.ulcSection20 === "yes") {
        ulcArea = parseFloat(formData.ulcArea) || plotAreaSqMtr;
        ulcCharges = ulcArea * rrRatePlot * 0.025;
      }

      // Total MHADA Costs
      const totalMhadaCosts = mhadaFSICost + titbitAreaCost + ancillaryFSICost + mhadaScrutiny + mhadaDebris + 
                              mhadaLayout + mhadaWaterDeposit + mhadaInfrastructure + mhadaPMSInfra +
                              mhadaSanctionCost + scrutinyCharges + radaRodaCharges + demolitionCharges +
                              waterLineCharges + cdCharges + nonFSICharges + ulcCharges;

      return {
        isMhada: true,
        baseFSIArea,
        deduction,
        mhadaFSIArea,
        mhadaFSICost,
        titbitArea,
        titbitAreaCost,
        proRataFSI,
        ancillaryFSICost,
        mhadaScrutiny,
        mhadaDebris,
        mhadaLayout,
        mhadaWaterDeposit,
        mhadaInfrastructure,
        mhadaPMSInfra,
        mhadaSanctionCost,
        scrutinyCharges,
        radaRodaCharges,
        demolitionCharges,
        waterLineCharges,
        cdCharges,
        nonFSICharges,
        nonFSIArea,
        ulcCharges,
        ulcArea,
        totalCosts: totalMhadaCosts,
        // Standard fields for compatibility
        amenityTDRCost: 0,
        slumTDRCost: 0,
        paidFSICost: 0,
        landDevCharges: 0,
        buildingDevCharges: 0,
        upkar: 0,
        fireFightingCharges: 0,
        incentiveFSI: 0,
        adjustedPaidFSIArea: 0,
        adjustedTDRArea: 0,
        existingRoadWidening: 0,
        existingBuiltupArea
      };
    }

    // ============ NON-MHADA (STANDARD) CALCULATIONS ============
    
    // ============ BASIC FSI-ONLY MODE (Feb 2026) ============
    // We ALWAYS compute the full breakdown with fb=fsiBreakdown (normal mode).
    // The Basic-FSI total is then derived by proportional scaling below.
    // If the user has enabled the toggle, we finally swap `totalCosts` to be the basic value.
    const basicFSIOnly = formData.calculateAtBasicFSIOnly === "yes";
    const fb = fsiBreakdown;  // always normal for line-item calcs

    // Check if narrow road (< 9m) - from backend FSI calculation
    const isNarrowRoad = fb.isNarrowRoad || false;
    const narrowRoadIncentiveFSI = fb.incentiveFSIArea || 0;
    
    // Check if building > 30 years for incentive FSI
    let incentiveFSI = 0;
    let adjustedPaidFSIArea = fb.paidFSIArea;
    let adjustedTDRArea = fb.tdrArea;

    // Original TDR areas from FSI breakdown
    const originalAmenityTDRArea = fb.amenityTDR * fb.grossPlotArea;
    const originalSlumTDRArea = fb.slumTDR * fb.grossPlotArea;

    // Start with original areas, then apply incentive FSI deduction
    let amenityTDRAreaAfterIncentive = originalAmenityTDRArea;
    let slumTDRAreaAfterIncentive = originalSlumTDRArea;

    // For incentive FSI: use existing built-up area if provided, otherwise use plot area
    const existingBuiltupForIncentive = parseFloat(formData.existingBuiltupArea) || plotAreaSqMtr;

    // For narrow roads (< 9m): Incentive FSI is already calculated by backend
    // No TDR, No Paid FSI - these will be 0 from backend
    if (isNarrowRoad) {
      incentiveFSI = narrowRoadIncentiveFSI;
      adjustedPaidFSIArea = 0;  // No Paid FSI for narrow roads
      adjustedTDRArea = 0;  // No TDR for narrow roads
      amenityTDRAreaAfterIncentive = 0;
      slumTDRAreaAfterIncentive = 0;
    } else if (buildingAge > 30) {
      // Incentive FSI = Existing Built-up Area × 30% (or Plot Area if not provided)
      incentiveFSI = existingBuiltupForIncentive * 0.30;
      const totalPaidTDR = fb.paidFSI + fb.totalTDR;
      if (totalPaidTDR > 0) {
        const paidProportion = fb.paidFSI / totalPaidTDR;
        const tdrProportion = fb.totalTDR / totalPaidTDR;
        const incentiveReductionPaid = incentiveFSI * paidProportion;
        const incentiveReductionTDR = incentiveFSI * tdrProportion;
        adjustedPaidFSIArea = Math.max(0, fb.paidFSIArea - incentiveReductionPaid);
        adjustedTDRArea = Math.max(0, fb.tdrArea - incentiveReductionTDR);

        // Proportionally split TDR incentive reduction between Amenity and Slum TDR
        const totalOriginalTDRArea = originalAmenityTDRArea + originalSlumTDRArea;
        if (totalOriginalTDRArea > 0) {
          const amenityProportion = originalAmenityTDRArea / totalOriginalTDRArea;
          const slumProportion = originalSlumTDRArea / totalOriginalTDRArea;
          amenityTDRAreaAfterIncentive = Math.max(0, originalAmenityTDRArea - (incentiveReductionTDR * amenityProportion));
          slumTDRAreaAfterIncentive = Math.max(0, originalSlumTDRArea - (incentiveReductionTDR * slumProportion));
        }
      }
    }

    // Road widening adjustment:
    // Road widening TDR reduction applies ONLY to Amenity TDR (not Slum TDR)
    // Old road widening at 1:1 factor, New road widening at 1:2 factor
    const tdrReductionFromRoadWidening = fb.tdrReductionFromRoadWidening || 0;
    
    // Existing TDR already used - deduct from TDR to be purchased
    const existingTDRArea = formData.hasExistingTDR === "yes" ? (parseFloat(formData.existingTDRArea) || 0) : 0;
    
    // 1. Amenity TDR Cost - REDUCED by incentive FSI (proportional), road widening, AND existing TDR
    const amenityTDRPercent = parseFloat(formData.amenityTDRPercent) || 0;
    const amenityTDRArea = originalAmenityTDRArea;
    
    // Calculate existing TDR deduction proportionally between Amenity and Slum
    let existingTDRDeductionAmenity = 0;
    let existingTDRDeductionSlum = 0;
    if (existingTDRArea > 0) {
      const totalTDRAfterIncentive = amenityTDRAreaAfterIncentive + slumTDRAreaAfterIncentive;
      if (totalTDRAfterIncentive > 0) {
        const amenityProportion = amenityTDRAreaAfterIncentive / totalTDRAfterIncentive;
        const slumProportion = slumTDRAreaAfterIncentive / totalTDRAfterIncentive;
        existingTDRDeductionAmenity = existingTDRArea * amenityProportion;
        existingTDRDeductionSlum = existingTDRArea * slumProportion;
      }
    }
    
    // Apply road widening reduction AND existing TDR reduction on top of incentive reduction
    const adjustedAmenityTDRArea = Math.max(0, amenityTDRAreaAfterIncentive - tdrReductionFromRoadWidening - existingTDRDeductionAmenity);
    const amenityTDRCost = adjustedAmenityTDRArea * rrRatePlot * (amenityTDRPercent / 100);

    // 2. Slum TDR Cost - REDUCED by incentive FSI (proportional) AND existing TDR, NOT affected by road widening
    let slumTDRCost = 0;
    const slumTDRArea = originalSlumTDRArea;
    const adjustedSlumTDRArea = Math.max(0, slumTDRAreaAfterIncentive - existingTDRDeductionSlum);
    const slumTDRPercent = parseFloat(formData.slumTDRRRPercent) || 0;
    if (formData.slumTDRMethod === "zone_drc") {
      // Zone DRC method: user enters rate in ₹/sft, convert to per sq mtr
      const zoneDRCRate = parseFloat(formData.slumTDRZoneDRC) || 0;
      const zoneDRCRatePerSqMtr = zoneDRCRate * 10.764;
      slumTDRCost = adjustedSlumTDRArea * zoneDRCRatePerSqMtr;
    } else {
      // Percentage of RR method (like Amenity TDR): Area × RR Rate × %
      slumTDRCost = adjustedSlumTDRArea * rrRatePlot * (slumTDRPercent / 100);
    }

    // 3. Ancillary FSI Cost - 15% of land RR rate
    const ancillaryFSICost = fb.totalAncillary * rrRatePlot * 0.15;

    // 4. Paid FSI Cost - 35% of land RR rate
    const paidFSICost = adjustedPaidFSIArea * rrRatePlot * 0.35;

    // 4b. CBD Premium FSI Cost - additional FSI above non-TOD baseline @ 50% of land RR rate.
    // Applies ONLY when classification === 'cbd'. Sits alongside Paid FSI + TDR (both still charged normally).
    const cbdPremiumArea = fsiBreakdown?.cbdPremiumArea || 0;
    const cbdPremiumCost = cbdPremiumArea * rrRatePlot * 0.50;

    // 5. Land Development Charges - 1% residential, 2% commercial
    const landDevRate = isCommercial ? 0.02 : (0.01 + (commercialPercentage / 100) * 0.01);
    const landDevCharges = fb.grossPlotArea * rrRatePlot * landDevRate;

    // 6. Building Development Charges - 4% residential, 8% commercial
    const buildingDevRate = isCommercial ? 0.08 : (0.04 + (commercialPercentage / 100) * 0.04);
    const buildingDevCharges = fb.totalFSIWithAncillary * rrRatePlot * buildingDevRate;

    // 7. Labour Cess (Upkar) - 1% residential, 1.5% commercial
    const labourCessRate = isCommercial ? 0.015 : (0.01 + (commercialPercentage / 100) * 0.005);
    const upkar = fb.totalFSIWithAncillary * rrRateConstruction * labourCessRate;

    // 8. Fire Fighting Charges
    const fireFightingRate = parseFloat(formData.fireFightingRate) || 0;
    const fireFightingCharges = fb.totalFSIWithAncillary * fireFightingRate;

    // ============ ADMIN-CONFIGURABLE RATES (Feb 2026) ============
    const cfg = (appConfig && appConfig.fsi_costs) || {};
    const SCRUTINY_PER_SQM = cfg.scrutiny_per_sqm ?? 5;
    const RADA_RODA_PER_SQM = cfg.rada_roda_per_sqm ?? 20;
    const TDR_SCRUTINY_PER_SQM = cfg.tdr_scrutiny_per_sqm ?? 30;
    const DEMOLITION_WASTE_AREA_PCT = (cfg.demolition_waste_area_percent ?? 5) / 100;
    const DEMOLITION_WASTE_PER_SQM = cfg.demolition_waste_per_sqm ?? 227;
    const LIFT_AREA_PER_LIFT_SQM = cfg.lift_machine_room_area_per_lift_sqm ?? 8;
    const LIFT_LAND_RR_PCT = (cfg.lift_machine_room_land_rr_percent ?? 60) / 100;
    const C2_CONVERSION_PLOT_PCT = (cfg.c2_conversion_plot_percent ?? 40) / 100;
    const WATER_LINE_PER_SQRT_PLOT = cfg.water_line_per_sqrt_plot ?? 3640;

    // ============ NEW CHARGES ============

    // 9. Scrutiny Charges - Total FSI × admin rate
    const scrutinyCharges = fb.totalFSIWithAncillary * SCRUTINY_PER_SQM;

    // 10. RADA/RODA Charges - Total FSI × admin rate
    const radaRodaCharges = fb.totalFSIWithAncillary * RADA_RODA_PER_SQM;

    // 11. Demolition Charges - Existing Built-up × 50 / 1000 × 227 (legacy formula)
    const existingBuiltupArea = parseFloat(formData.existingBuiltupArea) || parseFloat(formData.plotAreaPerCard) || 0;
    const demolitionCharges = formData.projectMode === "greenfield" ? 0 : (existingBuiltupArea * 50 / 1000 * 227);

    // 12. Water Line Charges - √(Plot Area) × admin rate
    const waterLineCharges = Math.sqrt(plotAreaSqMtr) * WATER_LINE_PER_SQRT_PLOT;

    // 13. C&D Charges - Total FSI × area% × per-sqm rate (admin editable)
    const cdCharges = fb.totalFSIWithAncillary * DEMOLITION_WASTE_AREA_PCT * DEMOLITION_WASTE_PER_SQM;

    // 14. Non-FSI Area Charges
    // (Car Parking × 20 + 2W Parking × 4) × 1% × Construction RR
    const fourWheelerPerMember = parseFloat(formData.fourWheelerPerMember) || 1;
    const twoWheelerPerMember = parseFloat(formData.twoWheelerPerMember) || 1;
    const totalCarParking = numMembers * fourWheelerPerMember;
    const total2WheelerParking = numMembers * twoWheelerPerMember;
    const nonFSIArea = (totalCarParking * 20) + (total2WheelerParking * 4);
    const nonFSICharges = nonFSIArea * 0.01 * rrRateConstruction;

    // 15. ULC Section 20 Charges - Area × Land RR × 2.5%
    let ulcCharges = 0;
    let ulcArea = 0;
    if (formData.ulcSection20 === "yes") {
      ulcArea = parseFloat(formData.ulcArea) || plotAreaSqMtr;
      ulcCharges = ulcArea * rrRatePlot * 0.025;
    }

    // ============ NEW CHARGES (Feb 2026) ============

    // 16. TDR Scrutiny Charges - admin rate × total TDR area (sqm)
    const tdrAreaSqm = fb.tdrArea || 0;
    const tdrScrutinyCharges = tdrAreaSqm * TDR_SCRUTINY_PER_SQM;

    // 17. Lift Machine Room - 8 sqm × num_lifts × 60% × land RR
    const numberOfLifts = parseInt(formData.numberOfLifts) || 0;
    const liftMachineRoomCharges = LIFT_AREA_PER_LIFT_SQM * numberOfLifts * LIFT_LAND_RR_PCT * rrRatePlot;

    // 18. C2 Zone Conversion Charges - 40% × plot × land RR (commercial / mixed only, when isC2Zone === "yes")
    const isC2Zone = (formData.projectType === "commercial" || formData.projectType === "mixed") && formData.isC2Zone === "yes";
    const c2ConversionCharges = isC2Zone ? (plotAreaSqMtr * C2_CONVERSION_PLOT_PCT * rrRatePlot) : 0;

    // Total FSI and Sanction Costs (NORMAL mode — always computed with full FSI)
    const totalCostsNormalMode = amenityTDRCost + slumTDRCost + ancillaryFSICost + 
                       paidFSICost + cbdPremiumCost + landDevCharges + buildingDevCharges + upkar + fireFightingCharges +
                       scrutinyCharges + radaRodaCharges + demolitionCharges + waterLineCharges + 
                       cdCharges + nonFSICharges + ulcCharges +
                       tdrScrutinyCharges + liftMachineRoomCharges + c2ConversionCharges;

    // ============ BASIC-FSI-ONLY TOTAL (derived by scaling normal-mode line items) ============
    // Line items that scale with totalFSIWithAncillary → reduce proportionally in basic mode
    const areaScaledNormal = ancillaryFSICost + buildingDevCharges + upkar + fireFightingCharges +
                             scrutinyCharges + radaRodaCharges + cdCharges;
    // Line items that stay CONSTANT across modes (plot / lift / parking / building age based)
    const areaIndependent = landDevCharges + demolitionCharges + waterLineCharges +
                            nonFSICharges + ulcCharges + liftMachineRoomCharges + c2ConversionCharges;
    // Compute basic-mode area ratio: (basic buildable + FULL ancillary from normal sanction) / (normal totalFSIWithAnc)
    // Ancillary FSI is a granted benefit — kept at full amount even when only Basic FSI is used.
    const basicScale = (() => {
      const _basic = fsiBreakdown.basicFSIArea || 0;
      const _green = fsiBreakdown.greenFSIArea || 0;
      const _buildable = _basic + _green;
      // Full ancillary (not scaled proportionally) — as per user requirement
      const _fullAnc = fsiBreakdown.totalAncillary || 0;
      const _basicWithAnc = _buildable + _fullAnc;
      return fsiBreakdown.totalFSIWithAncillary > 0
        ? _basicWithAnc / fsiBreakdown.totalFSIWithAncillary
        : 0;
    })();
    // In basic mode: no Paid FSI, no TDR (amenity + slum), no TDR scrutiny
    const totalCostsBasicMode = (areaScaledNormal * basicScale) + areaIndependent;

    // The active `totalCosts` is ALWAYS the normal full-FSI value — this flows into P&L
    // and Total Expenses. The "Basic FSI only" toggle is now purely informational
    // (displays the reduced total in the summary block for what-if comparison only).
    const totalCosts = totalCostsNormalMode;

    return {
      amenityTDRCost,
      amenityTDRArea,
      adjustedAmenityTDRArea,
      amenityTDRPercent,
      tdrReductionFromRoadWidening,
      existingTDRArea,
      existingTDRDeductionAmenity,
      existingTDRDeductionSlum,
      slumTDRCost,
      slumTDRArea,
      adjustedSlumTDRArea,
      slumTDRMethod: formData.slumTDRMethod || "zone_drc",
      slumTDRRRPercent: parseFloat(formData.slumTDRRRPercent) || 0,
      slumTDRZoneDRC: parseFloat(formData.slumTDRZoneDRC) || 0,
      rrRatePlot,
      rrRateConstruction,
      isCommercial,
      ancillaryFSICost,
      paidFSICost,
      cbdPremiumCost,
      cbdPremiumArea,
      landDevCharges,
      buildingDevCharges,
      upkar,
      fireFightingCharges,
      // New charges
      scrutinyCharges,
      radaRodaCharges,
      demolitionCharges,
      waterLineCharges,
      cdCharges,
      nonFSICharges,
      nonFSIArea,
      ulcCharges,
      ulcArea,
      // Feb 2026 new charges
      tdrScrutinyCharges,
      liftMachineRoomCharges,
      numberOfLifts,
      c2ConversionCharges,
      isC2Zone,
      // Totals
      totalCosts,
      totalCostsNormalMode,
      totalCostsBasicMode,
      basicFSIOnly,
      incentiveFSI,
      adjustedPaidFSIArea,
      adjustedTDRArea,
      existingBuiltupArea,
      existingBuiltupForIncentive,
      isNarrowRoad
    };
  }, [formData, fsiBreakdown, appConfig]);

  // Pass costs to parent
  useEffect(() => {
    if (setFSICosts && costBreakdown) {
      setFSICosts({ ...costBreakdown, _fromComponent: true });
    }
  }, [costBreakdown, setFSICosts]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const exportFSICostsPDF = () => {
    if (!costBreakdown) return;
    const cb = costBreakdown;
    const fc = (v) => formatCurrency(v);
    const projectName = formData.projectName || formData.societyName || "Project";

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    let y = 20;

    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("FSI & Sanction Costs", pageWidth / 2, y, { align: "center" });
    y += 8;
    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    doc.text(projectName, pageWidth / 2, y, { align: "center" });
    y += 6;
    doc.setFontSize(9);
    doc.setTextColor(100);
    doc.text(`Generated: ${new Date().toLocaleDateString('en-IN')}`, pageWidth / 2, y, { align: "center" });
    doc.setTextColor(0);
    y += 10;

    // TDR Costs (skip for narrow roads)
    if (!cb.isNarrowRoad) {
    doc.autoTable({
      startY: y,
      head: [["TDR Costs", "Amount"]],
      body: [
        [`Amenity TDR (${cb.adjustedAmenityTDRArea?.toFixed(0)} sqm × ₹${cb.rrRatePlot?.toLocaleString('en-IN')} × ${cb.amenityTDRPercent}%)`, fc(cb.amenityTDRCost)],
        [`Slum TDR (${cb.adjustedSlumTDRArea?.toFixed(0)} sqm)`, fc(cb.slumTDRCost)],
      ],
      theme: "grid",
      headStyles: { fillColor: [30, 64, 175], fontSize: 10 },
      styles: { fontSize: 9, cellPadding: 3 },
      columnStyles: { 1: { halign: "right" } },
    });
    y = doc.lastAutoTable.finalY + 8;
    }

    // FSI Premiums
    const fsiPremiumRows = [
      ["Ancillary FSI (15% of Land RR)", fc(cb.ancillaryFSICost)],
    ];
    if (!cb.isNarrowRoad) {
      fsiPremiumRows.unshift(["Paid FSI (35% of Land RR)", fc(cb.paidFSICost)]);
      if (formData.projectClassification === "cbd" && cb.cbdPremiumCost > 0) {
        fsiPremiumRows.push(["CBD Premium FSI (50% of Land RR)", fc(cb.cbdPremiumCost)]);
      }
    }
    doc.autoTable({
      startY: y,
      head: [["FSI Premiums", "Amount"]],
      body: fsiPremiumRows,
      theme: "grid",
      headStyles: { fillColor: [30, 64, 175], fontSize: 10 },
      styles: { fontSize: 9, cellPadding: 3 },
      columnStyles: { 1: { halign: "right" } },
    });
    y = doc.lastAutoTable.finalY + 8;

    // Other Charges
    doc.autoTable({
      startY: y,
      head: [["Other Sanction Charges", "Amount"]],
      body: [
        ["Land Development Charges", fc(cb.landDevCharges)],
        ["Building Development Charges", fc(cb.buildingDevCharges)],
        ["Labour Cess (Upkar)", fc(cb.upkar)],
        ["Fire Fighting Charges", fc(cb.fireFightingCharges)],
        ["Scrutiny Charges", fc(cb.scrutinyCharges)],
        ["RADA/RODA Charges", fc(cb.radaRodaCharges)],
        ["Demolition Charges", fc(cb.demolitionCharges)],
        ["Water Line Charges", fc(cb.waterLineCharges)],
        ["C&D Charges", fc(cb.cdCharges)],
        ["Non-FSI Area Charges", fc(cb.nonFSICharges)],
        ...(cb.ulcCharges > 0 ? [["ULC Sec 20 Charges", fc(cb.ulcCharges)]] : []),
      ],
      foot: [["TOTAL FSI & SANCTION COSTS", fc(cb.totalCosts)]],
      theme: "grid",
      headStyles: { fillColor: [22, 163, 74], fontSize: 10 },
      footStyles: { fillColor: [22, 163, 74], textColor: [255, 255, 255], fontStyle: "bold", fontSize: 11 },
      styles: { fontSize: 9, cellPadding: 3 },
      columnStyles: { 1: { halign: "right" } },
    });

    doc.save(`FSI_Costs_${projectName.replace(/\s+/g, '_')}.pdf`);
    toast.success("FSI Costs exported as PDF");
  };

  return (
    <div className="space-y-6">
      {/* TDR & FSI Inputs - Read-only from Quick Input (hidden for narrow roads) */}
      {!fsiBreakdown?.isNarrowRoad && formData.projectClassification === "cbd" && (
        <Card className="rounded-sm shadow-sm border-emerald-400/50 bg-emerald-50/40" data-testid="cbd-fsi-breakdown">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading tracking-tight text-emerald-800 flex items-center gap-2">
              CBD FSI Breakdown
            </CardTitle>
            <CardDescription className="text-emerald-700">
              Non-TOD baseline FSI (Paid FSI + TDR + Incentive) is calculated first as usual. The <strong>additional CBD FSI</strong> above the baseline sits on top as a Premium layer, chargeable at <strong>50% of Land RR rate</strong>.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-4 gap-3 text-sm">
              <div className="p-3 rounded-sm bg-white/70 border border-emerald-200">
                <p className="text-[11px] uppercase tracking-wide text-emerald-700">Basic FSI</p>
                <p className="font-mono font-bold text-emerald-900 text-base">1.10</p>
                <p className="text-[11px] text-muted-foreground mt-1">Free — no charge</p>
              </div>
              <div className="p-3 rounded-sm bg-white/70 border border-emerald-200">
                <p className="text-[11px] uppercase tracking-wide text-emerald-700">Non-TOD Baseline</p>
                <p className="font-mono font-bold text-emerald-900 text-base">
                  {(fsiBreakdown?.cbdBaselineFSI || 0).toFixed(2)}
                </p>
                <p className="text-[11px] text-muted-foreground mt-1">Paid FSI + TDR (normal split)</p>
              </div>
              <div className="p-3 rounded-sm bg-amber-50 border border-amber-300">
                <p className="text-[11px] uppercase tracking-wide text-amber-700">CBD Premium FSI</p>
                <p className="font-mono font-bold text-amber-900 text-base">
                  +{(fsiBreakdown?.cbdPremiumFSI || 0).toFixed(2)}
                </p>
                <p className="text-[11px] text-muted-foreground mt-1">Paid @ 50% of Land RR</p>
              </div>
              <div className="p-3 rounded-sm bg-emerald-100 border border-emerald-400">
                <p className="text-[11px] uppercase tracking-wide text-emerald-800">Total Permissible</p>
                <p className="font-mono font-bold text-emerald-900 text-base">
                  {(fsiBreakdown?.permissibleFSI || 0).toFixed(2)}
                </p>
                <p className="text-[11px] text-muted-foreground mt-1">
                  {(() => {
                    const rw = parseFloat(formData.roadWidth) || 0;
                    if (rw >= 27) return "Road ≥ 27m → 5.0";
                    if (rw >= 18) return "Road 18–27m → 4.0";
                    if (rw >= 12) return "Road 12–18m → 3.0";
                    return "Road < 12m → 2.0";
                  })()}
                </p>
              </div>
            </div>
            {costBreakdown?.cbdPremiumCost > 0 && (
              <div className="mt-3 p-3 rounded-sm bg-amber-100/60 border border-amber-300 text-sm">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-amber-900">CBD Premium FSI Cost</span>
                  <span className="font-mono font-bold text-amber-900" data-testid="cbd-premium-cost">
                    ₹{(costBreakdown.cbdPremiumCost / 10000000).toFixed(2)} Cr
                  </span>
                </div>
                <p className="text-[11px] text-amber-800 mt-1">
                  = {(fsiBreakdown?.cbdPremiumArea || 0).toFixed(0)} sqm × ₹{costBreakdown.rrRatePlot?.toLocaleString('en-IN')} × 50%
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {!fsiBreakdown?.isNarrowRoad && (
      <Card className="rounded-sm shadow-sm">
        <CardHeader>
          <CardTitle className="font-heading tracking-tight">TDR & FSI Cost Inputs</CardTitle>
          <CardDescription>Values from Quick Input page — change there to update</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-3 text-sm">
            <div className="flex justify-between p-2 bg-secondary/50 rounded-sm">
              <span className="text-muted-foreground">Amenity TDR (% of Land RR)</span>
              <span className="font-semibold">{formData.amenityTDRPercent || 0}%</span>
            </div>
            <div className="flex justify-between p-2 bg-secondary/50 rounded-sm">
              <span className="text-muted-foreground">Slum TDR Method</span>
              <span className="font-semibold">{formData.slumTDRMethod === "zone_drc" ? `Zone DRC: ₹${parseInt(formData.slumTDRZoneDRC || 0).toLocaleString('en-IN')}/sft` : `% of Land RR: ${parseInt(formData.slumTDRRRPercent || 0)}%`}</span>
            </div>
            <div className="flex justify-between p-2 bg-secondary/50 rounded-sm">
              <span className="text-muted-foreground">Construction Cost</span>
              <span className="font-semibold">₹{parseInt(formData.constructionCostPerSqft || 0).toLocaleString('en-IN')}/sft</span>
            </div>
            <div className="flex justify-between p-2 bg-secondary/50 rounded-sm">
              <span className="text-muted-foreground">RR Rate (Plot)</span>
              <span className="font-semibold">₹{parseInt(formData.rrRatePlot || 0).toLocaleString('en-IN')}/sq mtr</span>
            </div>
            <div className="flex justify-between p-2 bg-secondary/50 rounded-sm">
              <span className="text-muted-foreground">RR Multiplier</span>
              <span className="font-semibold">
                {parseFloat(formData.rrMultiplier) || 5}%
                {formData.applyRRMarkup === "yes" && (parseFloat(formData.rrAdditionalMarkupPercent) || 0) > 0 && (
                  <span className="text-xs text-muted-foreground ml-1">(+ {parseFloat(formData.rrAdditionalMarkupPercent)}% markup)</span>
                )}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
      )}
      {fsiBreakdown?.isNarrowRoad && (
        <Card className="rounded-sm shadow-sm border-amber-400/50">
          <CardHeader>
            <CardTitle className="font-heading tracking-tight text-amber-700">Narrow Road (&lt; 9m) — No TDR / Paid FSI</CardTitle>
            <CardDescription>TDR and Paid FSI are not applicable for roads narrower than 9 meters</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-3 text-sm">
              <div className="flex justify-between p-2 bg-secondary/50 rounded-sm">
                <span className="text-muted-foreground">Construction Cost</span>
                <span className="font-semibold">₹{parseInt(formData.constructionCostPerSqft || 0).toLocaleString('en-IN')}/sft</span>
              </div>
              <div className="flex justify-between p-2 bg-secondary/50 rounded-sm">
                <span className="text-muted-foreground">RR Rate (Plot)</span>
                <span className="font-semibold">₹{parseInt(formData.rrRatePlot || 0).toLocaleString('en-IN')}/sq mtr</span>
              </div>
              <div className="flex justify-between p-2 bg-secondary/50 rounded-sm">
                <span className="text-muted-foreground">RR Multiplier</span>
                <span className="font-semibold">
                  {parseFloat(formData.rrMultiplier) || 5}%
                  {formData.applyRRMarkup === "yes" && (parseFloat(formData.rrAdditionalMarkupPercent) || 0) > 0 && (
                    <span className="text-xs text-muted-foreground ml-1">(+ {parseFloat(formData.rrAdditionalMarkupPercent)}% markup)</span>
                  )}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* MHADA Specific Costs */}
      {formData.projectClassification === "mhada" && (
        <Card className="rounded-sm shadow-sm border-chart-1/50">
          <CardHeader>
            <CardTitle className="font-heading tracking-tight text-chart-1">MHADA Project Costs</CardTitle>
            <CardDescription>Additional costs for MHADA redevelopment projects</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="mhadaSanctionRate">Sanction Cost Rate (₹ per sft)</Label>
                <Input
                  id="mhadaSanctionRate"
                  name="mhadaSanctionRate"
                  type="number"
                  value={formData.mhadaSanctionRate}
                  onChange={handleChange}
                  placeholder="300"
                  className="rounded-sm"
                  data-testid="mhada-sanction-rate-input"
                />
                <p className="text-xs text-muted-foreground">Plot Area × 2 × Rate</p>
              </div>
            </div>
            <div className="text-sm text-muted-foreground space-y-1">
              <p>• MHADA Premium FSI = (Plot + Titbit) × 3 − (Existing Built-up + Titbit × 1.1)</p>
              <p>• MHADA FSI Cost = Payable FSI Area × Land RR × 45%</p>
              <p>• Pro-rata FSI: 26.2 sq mtr per tenement (free)</p>
              <p>• Scrutiny: ₹27,000/bldg | Debris: ₹6,600/bldg | Layout: ₹1,000/tenement</p>
              <p>• Water Deposit: ₹1,00,000/bldg | Infrastructure: 7% × Land RR × FSI × 50% (+ PMS)</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Cost Breakdown */}
      {costBreakdown && (
        <Card className="rounded-sm shadow-sm border-accent/50" data-testid="fsi-costs-breakdown">
          <CardHeader>
            <CardTitle className="font-heading tracking-tight text-accent">
              {costBreakdown.isMhada ? 'MHADA FSI & Costs Breakdown' : 'FSI & Sanction Costs Breakdown'}
            </CardTitle>
            {costBreakdown.isMhada && (
              <CardDescription className="text-chart-1">
                ✓ MHADA Project: Payable FSI = {costBreakdown.mhadaFSIArea?.toFixed(0)} sq mtr, Pro-rata = {costBreakdown.proRataFSI?.toFixed(2)} sq mtr
              </CardDescription>
            )}
            {fsiBreakdown?.isNarrowRoad && (
              <CardDescription className="text-amber-600">
                ⚠️ Road Width &lt; 9m: Only Base FSI (1.1) + 30% Incentive FSI. No TDR/Paid FSI allowed.
              </CardDescription>
            )}
            {!costBreakdown.isMhada && costBreakdown.incentiveFSI > 0 && (
              <CardDescription className="text-chart-1">
                ✓ 30% Incentive FSI applied: {costBreakdown.incentiveFSI.toFixed(2)} sq mtr
              </CardDescription>
            )}
            {!costBreakdown.isMhada && costBreakdown.existingRoadWidening > 0 && (
              <CardDescription className="text-chart-1">
                ✓ Road Widening deduction: {costBreakdown.existingRoadWidening.toFixed(2)} sq mtr from TDR
              </CardDescription>
            )}
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Incentive FSI Distribution Section - Before FSI Breakdown */}
            {/* Narrow Road Incentive FSI - simplified (no TDR/Paid FSI distribution) */}
            {!costBreakdown.isMhada && costBreakdown.isNarrowRoad && costBreakdown.incentiveFSI > 0 && (
              <>
                <div className="space-y-3">
                  <h4 className="font-semibold text-sm uppercase tracking-wider text-amber-700">Incentive FSI (Road Width &lt; 9m)</h4>
                  <div className="bg-amber-50 border border-amber-200 p-4 rounded-sm space-y-3">
                    <div className="text-sm space-y-2">
                      <p className="font-medium text-amber-800">30% of Existing Built-up Area</p>
                      <div className="pl-2 space-y-1 text-muted-foreground">
                        <div className="flex justify-between">
                          <span>Existing Built-up Area × 30%</span>
                          <span className="font-mono">{costBreakdown.existingBuiltupForIncentive?.toFixed(0)} × 0.30 = <strong className="text-amber-700">{costBreakdown.incentiveFSI?.toFixed(0)} sq mtr</strong></span>
                        </div>
                        {!parseFloat(formData.existingBuiltupArea) && (
                          <p className="text-xs italic">(Using Plot Area as Existing Built-up Area not specified)</p>
                        )}
                      </div>
                    </div>
                    <div className="text-xs text-amber-700 bg-amber-100/50 p-2 rounded-sm">
                      <strong>Note:</strong> For roads &lt; 9m: No TDR and No Paid FSI allowed. Only Base FSI (1.1) + 30% Incentive FSI on existing built-up. Green FSI on Basic FSI and Ancillary FSI are allowed.
                    </div>
                  </div>
                </div>
                <Separator />
              </>
            )}
            {/* Standard Incentive FSI (Building 30+ years, non-narrow road) */}
            {!costBreakdown.isMhada && !costBreakdown.isNarrowRoad && costBreakdown.incentiveFSI > 0 && (
              <>
                <div className="space-y-3">
                  <h4 className="font-semibold text-sm uppercase tracking-wider text-green-700">Incentive FSI Calculation & Distribution</h4>
                  <div className="bg-green-50 border border-green-200 p-4 rounded-sm space-y-3">
                    <div className="grid md:grid-cols-2 gap-4 text-sm">
                      <div className="space-y-2">
                        <p className="font-medium text-green-800">30% Incentive FSI (Building 30+ years)</p>
                        <div className="pl-2 space-y-1 text-muted-foreground">
                          <div className="flex justify-between">
                            <span>Existing Built-up Area × 30%</span>
                            <span className="font-mono">{costBreakdown.existingBuiltupForIncentive?.toFixed(0)} × 0.30 = <strong className="text-green-700">{costBreakdown.incentiveFSI?.toFixed(0)} sq mtr</strong></span>
                          </div>
                          {!parseFloat(formData.existingBuiltupArea) && (
                            <p className="text-xs italic">(Using Plot Area as Existing Built-up Area not specified)</p>
                          )}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <p className="font-medium text-green-800">Distribution to Paid FSI & TDR</p>
                        <div className="pl-2 space-y-1 text-muted-foreground">
                          <div className="flex justify-between">
                            <span>Paid FSI Proportion</span>
                            <span className="font-mono">
                              {((fsiBreakdown?.paidFSI / (fsiBreakdown?.paidFSI + fsiBreakdown?.totalTDR)) * 100).toFixed(1)}%
                              <span className="text-xs ml-1">({(costBreakdown.incentiveFSI * (fsiBreakdown?.paidFSI / (fsiBreakdown?.paidFSI + fsiBreakdown?.totalTDR))).toFixed(0)} sq mtr)</span>
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span>TDR Proportion</span>
                            <span className="font-mono">
                              {((fsiBreakdown?.totalTDR / (fsiBreakdown?.paidFSI + fsiBreakdown?.totalTDR)) * 100).toFixed(1)}%
                              <span className="text-xs ml-1">({(costBreakdown.incentiveFSI * (fsiBreakdown?.totalTDR / (fsiBreakdown?.paidFSI + fsiBreakdown?.totalTDR))).toFixed(0)} sq mtr)</span>
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <Separator />
                    
                    <div className="text-sm space-y-2">
                      <p className="font-medium text-green-800">After Proportionate Deduction of Incentive FSI:</p>
                      <div className="grid md:grid-cols-3 gap-4 bg-white/50 p-3 rounded-sm">
                        <div className="text-center">
                          <p className="text-xs text-muted-foreground">Paid FSI Area</p>
                          <p className="font-mono font-semibold">{fsiBreakdown?.paidFSIArea?.toFixed(0)} → <span className="text-green-700">{costBreakdown.adjustedPaidFSIArea?.toFixed(0)} sq mtr</span></p>
                          <p className="text-xs text-muted-foreground">(-{(fsiBreakdown?.paidFSIArea - costBreakdown.adjustedPaidFSIArea)?.toFixed(0)} sq mtr)</p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-muted-foreground">Cost of Amenity TDR</p>
                          <p className="font-mono font-semibold">{costBreakdown.amenityTDRArea?.toFixed(0)} → <span className="text-green-700">{costBreakdown.adjustedAmenityTDRArea?.toFixed(0)} sq mtr</span></p>
                          <p className="text-xs text-muted-foreground">(To be purchased at {formData.tdrSplitRatio || "70:30"})</p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-muted-foreground">Slum TDR Cost</p>
                          <p className="font-mono font-semibold">{costBreakdown.slumTDRArea?.toFixed(0)} → <span className="text-green-700">{costBreakdown.adjustedSlumTDRArea?.toFixed(0)} sq mtr</span></p>
                          <p className="text-xs text-muted-foreground">(To be purchased at {formData.tdrSplitRatio || "70:30"})</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="text-xs text-green-700 bg-green-100/50 p-2 rounded-sm">
                      <strong>Note:</strong> Balance TDR (after incentive deduction) is purchased as per TDR Split Ratio ({formData.tdrSplitRatio || "70:30"} Amenity:Slum) selected by the client.
                    </div>
                  </div>
                </div>
                <Separator />
              </>
            )}
            
            {/* MHADA Specific Breakdown */}
            {costBreakdown.isMhada ? (
              <>
                <div className="space-y-3">
                  <h4 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground">MHADA FSI Costs</h4>
                  <div className="space-y-2 text-sm bg-secondary/30 p-3 rounded-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">MHADA FSI ({costBreakdown.mhadaFSIArea?.toFixed(0)} sq mtr × Land RR × 45%)</span>
                      <span className="font-mono">{formatCurrency(costBreakdown.mhadaFSICost)}</span>
                    </div>
                    <div className="text-xs text-muted-foreground pl-2">
                      Base: (Plot + Titbit) × 3 = {costBreakdown.baseFSIArea?.toFixed(0)} | Less: Existing B/U + Titbit × 1.1 = {costBreakdown.deduction?.toFixed(0)} | Payable = {costBreakdown.mhadaFSIArea?.toFixed(0)} sq mtr
                    </div>
                    {costBreakdown.titbitAreaCost > 0 && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Titbit Area ({costBreakdown.titbitArea?.toFixed(0)} sq mtr × 50% × Land RR)</span>
                        <span className="font-mono">{formatCurrency(costBreakdown.titbitAreaCost)}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Ancillary FSI (15% of Land RR)</span>
                      <span className="font-mono">{formatCurrency(costBreakdown.ancillaryFSICost)}</span>
                    </div>
                  </div>
                </div>
                <Separator />
                <div className="space-y-3">
                  <h4 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground">MHADA Charges</h4>
                  <div className="space-y-2 text-sm bg-secondary/30 p-3 rounded-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Scrutiny (₹27,000/bldg)</span>
                      <span className="font-mono">{formatCurrency(costBreakdown.mhadaScrutiny)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Debris Removal (₹6,600/bldg)</span>
                      <span className="font-mono">{formatCurrency(costBreakdown.mhadaDebris)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Layout Approval (₹1,000/tenement)</span>
                      <span className="font-mono">{formatCurrency(costBreakdown.mhadaLayout)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Water Deposit (₹1,00,000/bldg)</span>
                      <span className="font-mono">{formatCurrency(costBreakdown.mhadaWaterDeposit)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Infrastructure (7% × FSI × 50%)</span>
                      <span className="font-mono">{formatCurrency(costBreakdown.mhadaInfrastructure)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">PMS Infrastructure</span>
                      <span className="font-mono">{formatCurrency(costBreakdown.mhadaPMSInfra)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Sanction Cost (Plot × 2 × Rate)</span>
                      <span className="font-mono">{formatCurrency(costBreakdown.mhadaSanctionCost)}</span>
                    </div>
                  </div>
                </div>
                <Separator />
                <div className="space-y-3">
                  <h4 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground">Statutory Charges</h4>
                  <div className="space-y-2 text-sm bg-secondary/30 p-3 rounded-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Scrutiny (FSI × ₹5)</span>
                      <span className="font-mono">{formatCurrency(costBreakdown.scrutinyCharges)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">RADA/RODA (FSI × ₹20)</span>
                      <span className="font-mono">{formatCurrency(costBreakdown.radaRodaCharges)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Demolition</span>
                      <span className="font-mono">{formatCurrency(costBreakdown.demolitionCharges)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Water Line</span>
                      <span className="font-mono">{formatCurrency(costBreakdown.waterLineCharges)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">C&D Charges</span>
                      <span className="font-mono">{formatCurrency(costBreakdown.cdCharges)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Non-FSI Area</span>
                      <span className="font-mono">{formatCurrency(costBreakdown.nonFSICharges)}</span>
                    </div>
                    {costBreakdown.ulcCharges > 0 && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">ULC Sec 20</span>
                        <span className="font-mono">{formatCurrency(costBreakdown.ulcCharges)}</span>
                      </div>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* FSI Distribution Breakdown */}
                <div className="space-y-3">
                  <h4 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground">FSI Distribution</h4>
                  {costBreakdown.isNarrowRoad && (
                    <div className="text-xs bg-amber-500/10 border border-amber-500/30 p-2 rounded-sm text-amber-700">
                      Road Width &lt; 9m: Base FSI (1.1) + 30% Incentive ({costBreakdown.incentiveFSI?.toFixed(0)} sqm) + Green FSI + Ancillary. No TDR / Paid FSI.
                    </div>
                  )}
                  {!costBreakdown.isNarrowRoad && costBreakdown.incentiveFSI > 0 && (
                    <div className="text-xs bg-green-500/10 border border-green-500/30 p-2 rounded-sm text-green-700">
                      Incentive FSI (0.30): {costBreakdown.incentiveFSI?.toFixed(0)} sqm deducted proportionately — Paid FSI reduced to {costBreakdown.adjustedPaidFSIArea?.toFixed(0)} sqm, TDR reduced to {costBreakdown.adjustedTDRArea?.toFixed(0)} sqm
                    </div>
                  )}
                  <div className="space-y-2 text-sm bg-secondary/30 p-3 rounded-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Basic FSI ({fsiBreakdown?.basicFSI?.toFixed(2)})</span>
                      <span className="font-mono">{fsiBreakdown?.basicFSIArea?.toFixed(0) || 0} sq mtr</span>
                    </div>
                    {fsiBreakdown?.incentiveFSIArea > 0 && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">
                          Incentive FSI (0.30) — {costBreakdown.isNarrowRoad ? 'Road < 9m (30% of Built-up)' : 'Building > 30 years'}
                        </span>
                        <span className="font-mono">{fsiBreakdown?.incentiveFSIArea?.toFixed(0) || 0} sq mtr</span>
                      </div>
                    )}
                    {!costBreakdown.isNarrowRoad && fsiBreakdown?.paidFSIArea > 0 && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Paid FSI ({fsiBreakdown?.paidFSI?.toFixed(2)})</span>
                        <span className="font-mono">{costBreakdown.adjustedPaidFSIArea?.toFixed(0) || fsiBreakdown?.paidFSIArea?.toFixed(0) || 0} sq mtr</span>
                      </div>
                    )}
                    {!costBreakdown.isNarrowRoad && costBreakdown.adjustedAmenityTDRArea > 0 && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Cost of Amenity TDR ({fsiBreakdown?.amenityTDR?.toFixed(2)})</span>
                        <span className="font-mono">{costBreakdown.adjustedAmenityTDRArea?.toFixed(0) || 0} sq mtr</span>
                      </div>
                    )}
                    {!costBreakdown.isNarrowRoad && costBreakdown.slumTDRArea > 0 && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{costBreakdown.slumTDRMethod === "rr_percent" ? "Slum TDR (% of RR)" : "Slum TDR (Zone DRC)"} ({fsiBreakdown?.slumTDR?.toFixed(2)})</span>
                        <span className="font-mono">{costBreakdown.adjustedSlumTDRArea?.toFixed(0) || costBreakdown.slumTDRArea?.toFixed(0) || 0} sq mtr</span>
                      </div>
                    )}
                    {fsiBreakdown?.greenFSIArea > 0 && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Green FSI ({fsiBreakdown?.greenFSI?.toFixed(3)})</span>
                        <span className="font-mono">{fsiBreakdown?.greenFSIArea?.toFixed(0) || 0} sq mtr</span>
                      </div>
                    )}
                    <Separator className="my-1" />
                    <div className="flex justify-between font-semibold">
                      <span>Total Buildable Area</span>
                      <span className="font-mono">{fsiBreakdown?.totalBuildableArea?.toFixed(0) || 0} sq mtr</span>
                    </div>
                    {fsiBreakdown?.totalAncillary > 0 && (
                      <>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Ancillary - Residential (60%)</span>
                          <span className="font-mono">{fsiBreakdown?.ancillaryResidential?.toFixed(0) || 0} sq mtr</span>
                        </div>
                        {fsiBreakdown?.ancillaryCommercial > 0 && (
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Ancillary - Commercial (80%)</span>
                            <span className="font-mono">{fsiBreakdown?.ancillaryCommercial?.toFixed(0) || 0} sq mtr</span>
                          </div>
                        )}
                        <Separator className="my-1" />
                        <div className="flex justify-between font-semibold">
                          <span>Total FSI with Ancillary</span>
                          <span className="font-mono">{fsiBreakdown?.totalFSIWithAncillary?.toFixed(0) || 0} sq mtr</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                <Separator />

                {/* Standard (Non-MHADA) Breakdown - TDR Costs (hidden for narrow roads) */}
                {!costBreakdown.isNarrowRoad && (
                <div className="space-y-3">
                  <h4 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground">TDR Costs</h4>
                  {costBreakdown.incentiveFSI > 0 && (
                    <div className="text-xs bg-green-500/10 border border-green-500/30 p-2 rounded-sm text-green-700">
                      30% Incentive FSI ({costBreakdown.incentiveFSI?.toFixed(0)} sqm) deducted proportionately from Paid FSI &amp; TDR
                    </div>
                  )}
                  <div className="space-y-2 text-sm bg-secondary/30 p-3 rounded-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        Cost of Amenity TDR
                        <span className="text-xs ml-1">
                          ({costBreakdown.adjustedAmenityTDRArea?.toFixed(0)} sqm × ₹{costBreakdown.rrRatePlot?.toLocaleString('en-IN')} × {costBreakdown.amenityTDRPercent}%)
                        </span>
                      </span>
                      <span className="font-mono">{formatCurrency(costBreakdown.amenityTDRCost)}</span>
                    </div>
                    {(costBreakdown.incentiveFSI > 0 || costBreakdown.tdrReductionFromRoadWidening > 0 || costBreakdown.existingTDRArea > 0) && costBreakdown.amenityTDRArea > costBreakdown.adjustedAmenityTDRArea && (
                      <div className="text-xs text-muted-foreground pl-2 border-l-2 border-muted">
                        Original: {costBreakdown.amenityTDRArea?.toFixed(0)} sqm
                        {costBreakdown.incentiveFSI > 0 && ` → Incentive: -${(costBreakdown.amenityTDRArea - (costBreakdown.adjustedAmenityTDRArea + costBreakdown.tdrReductionFromRoadWidening + (costBreakdown.existingTDRDeductionAmenity || 0))).toFixed(0)} sqm`}
                        {costBreakdown.tdrReductionFromRoadWidening > 0 && ` → Road widening: -${costBreakdown.tdrReductionFromRoadWidening.toFixed(0)} sqm`}
                        {costBreakdown.existingTDRDeductionAmenity > 0 && ` → Existing TDR: -${costBreakdown.existingTDRDeductionAmenity.toFixed(0)} sqm`}
                        {` = ${costBreakdown.adjustedAmenityTDRArea?.toFixed(0)} sqm`}
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        {costBreakdown.slumTDRMethod === "rr_percent" ? "Slum TDR (% of RR)" : "Slum TDR (Zone DRC)"}
                        <span className="text-xs ml-1">
                          ({costBreakdown.adjustedSlumTDRArea?.toFixed(0)} sqm × {costBreakdown.slumTDRMethod === "zone_drc" 
                            ? `₹${(costBreakdown.slumTDRZoneDRC * 10.764).toFixed(0)}/sqm` 
                            : `₹${costBreakdown.rrRatePlot?.toLocaleString('en-IN')} × ${costBreakdown.slumTDRRRPercent}%`})
                        </span>
                      </span>
                      <span className="font-mono">{formatCurrency(costBreakdown.slumTDRCost)}</span>
                    </div>
                    {(costBreakdown.incentiveFSI > 0 || costBreakdown.existingTDRDeductionSlum > 0) && costBreakdown.slumTDRArea > costBreakdown.adjustedSlumTDRArea && (
                      <div className="text-xs text-muted-foreground pl-2 border-l-2 border-muted">
                        Original: {costBreakdown.slumTDRArea?.toFixed(0)} sqm
                        {costBreakdown.incentiveFSI > 0 && ` → Incentive: -${(costBreakdown.slumTDRArea - costBreakdown.adjustedSlumTDRArea - (costBreakdown.existingTDRDeductionSlum || 0)).toFixed(0)} sqm`}
                        {costBreakdown.existingTDRDeductionSlum > 0 && ` → Existing TDR: -${costBreakdown.existingTDRDeductionSlum.toFixed(0)} sqm`}
                        {` = ${costBreakdown.adjustedSlumTDRArea?.toFixed(0)} sqm`}
                      </div>
                    )}
                  </div>
                </div>
                )}

                {!costBreakdown.isNarrowRoad && <Separator />}

                <div className="space-y-3">
                  <h4 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground">FSI Costs</h4>
                  <div className="space-y-2 text-sm bg-secondary/30 p-3 rounded-sm">
                    {!costBreakdown.isNarrowRoad && (
                    <>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        Paid FSI
                        <span className="text-xs ml-1">
                          ({costBreakdown.adjustedPaidFSIArea?.toFixed(0)} sqm × ₹{costBreakdown.rrRatePlot?.toLocaleString('en-IN')} × 35%)
                        </span>
                      </span>
                      <span className="font-mono">{formatCurrency(costBreakdown.paidFSICost)}</span>
                    </div>
                    {formData.projectClassification === "cbd" && costBreakdown.cbdPremiumArea > 0 && (
                      <div className="flex justify-between bg-amber-100/60 -mx-3 px-3 py-1 rounded-sm">
                        <span className="text-amber-900 font-medium">
                          CBD Premium FSI
                          <span className="text-xs ml-1 font-normal">
                            ({costBreakdown.cbdPremiumArea?.toFixed(0)} sqm × ₹{costBreakdown.rrRatePlot?.toLocaleString('en-IN')} × 50%)
                          </span>
                        </span>
                        <span className="font-mono text-amber-900">{formatCurrency(costBreakdown.cbdPremiumCost)}</span>
                      </div>
                    )}
                    {costBreakdown.incentiveFSI > 0 && fsiBreakdown?.paidFSIArea > costBreakdown.adjustedPaidFSIArea && (
                      <div className="text-xs text-muted-foreground pl-2 border-l-2 border-muted">
                        Original: {fsiBreakdown?.paidFSIArea?.toFixed(0)} sqm → Incentive deduction: -{(fsiBreakdown?.paidFSIArea - costBreakdown.adjustedPaidFSIArea).toFixed(0)} sqm = {costBreakdown.adjustedPaidFSIArea?.toFixed(0)} sqm
                      </div>
                    )}
                    </>
                    )}
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        Ancillary FSI
                        <span className="text-xs ml-1">
                          ({fsiBreakdown?.totalAncillary?.toFixed(0)} sqm × ₹{costBreakdown.rrRatePlot?.toLocaleString('en-IN')} × 15%)
                        </span>
                      </span>
                      <span className="font-mono">{formatCurrency(costBreakdown.ancillaryFSICost)}</span>
                    </div>
                  </div>
                </div>

                <Separator />

                <div className="space-y-3">
                  <h4 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground">Development Charges</h4>
                  <div className="space-y-2 text-sm bg-secondary/30 p-3 rounded-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        Land Development
                        <span className="text-xs ml-1">
                          ({fsiBreakdown?.grossPlotArea?.toFixed(0)} sqm × ₹{costBreakdown.rrRatePlot?.toLocaleString('en-IN')} × {((costBreakdown.isCommercial ? 2 : (1 + (parseFloat(formData.commercialPercentage) || 0) / 100)).toFixed(1))}%)
                        </span>
                      </span>
                      <span className="font-mono">{formatCurrency(costBreakdown.landDevCharges)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        Building Development
                        <span className="text-xs ml-1">
                          ({fsiBreakdown?.totalFSIWithAncillary?.toFixed(0)} sqm × ₹{costBreakdown.rrRatePlot?.toLocaleString('en-IN')} × {((costBreakdown.isCommercial ? 8 : (4 + (parseFloat(formData.commercialPercentage) || 0) / 100 * 4)).toFixed(1))}%)
                        </span>
                      </span>
                      <span className="font-mono">{formatCurrency(costBreakdown.buildingDevCharges)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        Labour Cess (Upkar)
                        <span className="text-xs ml-1">
                          ({fsiBreakdown?.totalFSIWithAncillary?.toFixed(0)} sqm × ₹{costBreakdown.rrRateConstruction?.toLocaleString('en-IN')} × {((costBreakdown.isCommercial ? 1.5 : (1 + (parseFloat(formData.commercialPercentage) || 0) / 100 * 0.5)).toFixed(1))}%)
                        </span>
                      </span>
                      <span className="font-mono">{formatCurrency(costBreakdown.upkar)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        Fire Fighting
                        <span className="text-xs ml-1">
                          ({fsiBreakdown?.totalFSIWithAncillary?.toFixed(0)} sqm × ₹{(parseFloat(formData.fireFightingRate) || 0).toLocaleString('en-IN')}/sqm)
                        </span>
                      </span>
                      <span className="font-mono">{formatCurrency(costBreakdown.fireFightingCharges)}</span>
                    </div>
                  </div>
                </div>

                <Separator />

                <div className="space-y-3">
                  <h4 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground">Statutory Charges</h4>
                  <div className="space-y-2 text-sm bg-secondary/30 p-3 rounded-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        Scrutiny
                        <span className="text-xs ml-1">({fsiBreakdown?.totalFSIWithAncillary?.toFixed(0)} sqm × ₹5)</span>
                      </span>
                      <span className="font-mono">{formatCurrency(costBreakdown.scrutinyCharges)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        RADA/RODA
                        <span className="text-xs ml-1">({fsiBreakdown?.totalFSIWithAncillary?.toFixed(0)} sqm × ₹20)</span>
                      </span>
                      <span className="font-mono">{formatCurrency(costBreakdown.radaRodaCharges)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        Demolition
                        <span className="text-xs ml-1">({costBreakdown.existingBuiltupArea?.toFixed(0)} sqm × 50/1000 × ₹227)</span>
                      </span>
                      <span className="font-mono">{formatCurrency(costBreakdown.demolitionCharges)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        Water Line
                        <span className="text-xs ml-1">(√{fsiBreakdown?.grossPlotArea?.toFixed(0)} × ₹3,640)</span>
                      </span>
                      <span className="font-mono">{formatCurrency(costBreakdown.waterLineCharges)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        C&D Charges
                        <span className="text-xs ml-1">({fsiBreakdown?.totalFSIWithAncillary?.toFixed(0)} sqm × 0.05 × ₹227)</span>
                      </span>
                      <span className="font-mono">{formatCurrency(costBreakdown.cdCharges)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        Non-FSI Area
                        <span className="text-xs ml-1">({costBreakdown.nonFSIArea?.toFixed(0)} sqm × 1% × ₹{costBreakdown.rrRateConstruction?.toLocaleString('en-IN')})</span>
                      </span>
                      <span className="font-mono">{formatCurrency(costBreakdown.nonFSICharges)}</span>
                    </div>
                    {costBreakdown.ulcCharges > 0 && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">
                          ULC Sec 20
                          <span className="text-xs ml-1">({costBreakdown.ulcArea?.toFixed(0)} sqm × ₹{costBreakdown.rrRatePlot?.toLocaleString('en-IN')} × 2.5%)</span>
                        </span>
                        <span className="font-mono">{formatCurrency(costBreakdown.ulcCharges)}</span>
                      </div>
                    )}
                    {costBreakdown.tdrScrutinyCharges > 0 && (
                      <div className="flex justify-between" data-testid="tdr-scrutiny-row">
                        <span className="text-muted-foreground">
                          TDR Scrutiny
                          <span className="text-xs ml-1">({(fsiBreakdown?.tdrArea || 0).toFixed(0)} sqm TDR × admin rate)</span>
                        </span>
                        <span className="font-mono">{formatCurrency(costBreakdown.tdrScrutinyCharges)}</span>
                      </div>
                    )}
                    {costBreakdown.liftMachineRoomCharges > 0 && (
                      <div className="flex justify-between" data-testid="lift-machine-room-row">
                        <span className="text-muted-foreground">
                          Lift Machine Room
                          <span className="text-xs ml-1">({costBreakdown.numberOfLifts} lifts × 8 sqm × 60% × Land RR)</span>
                        </span>
                        <span className="font-mono">{formatCurrency(costBreakdown.liftMachineRoomCharges)}</span>
                      </div>
                    )}
                    {costBreakdown.isC2Zone && costBreakdown.c2ConversionCharges > 0 && (
                      <div className="flex justify-between bg-amber-50 px-2 py-1 rounded-sm" data-testid="c2-conversion-row">
                        <span className="text-muted-foreground">
                          C2 Zone Conversion
                          <span className="text-xs ml-1">(40% of plot × ₹{costBreakdown.rrRatePlot?.toLocaleString('en-IN')}/sqm)</span>
                        </span>
                        <span className="font-mono">{formatCurrency(costBreakdown.c2ConversionCharges)}</span>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* Total — always uses FULL FSI cost for feasibility; Basic FSI shown as comparison */}
            <div className="p-4 rounded-sm border bg-accent/10 border-accent/30">
              <div className="flex justify-between items-center mb-3">
                <span className="font-semibold text-lg">
                  Total FSI &amp; Sanction Costs
                </span>
                <span className="font-mono font-bold text-2xl text-accent">{formatCurrency(costBreakdown.totalCosts)}</span>
              </div>
              {/* Side-by-side scenario comparison */}
              <div className="grid grid-cols-2 gap-3 pt-3 border-t border-current/10" data-testid="fsi-cost-both-modes">
                <div className="p-2 rounded-sm bg-white/60 ring-1 ring-accent/40">
                  <div className="text-[11px] text-muted-foreground uppercase tracking-wide">Normal (Full FSI)</div>
                  <div className="font-mono font-bold text-base" data-testid="total-normal-mode">{formatCurrency(costBreakdown.totalCostsNormalMode)}</div>
                  <div className="text-[10px] text-accent font-semibold">← Used in P&amp;L / feasibility</div>
                </div>
                <div className="p-2 rounded-sm bg-white/40">
                  <div className="text-[11px] text-muted-foreground uppercase tracking-wide">Basic FSI 1.1 only</div>
                  <div className="font-mono font-bold text-base" data-testid="total-basic-mode">{formatCurrency(costBreakdown.totalCostsBasicMode)}</div>
                  <div className="text-[10px] text-blue-700 font-semibold">Info only — not used in feasibility</div>
                </div>
              </div>
              <div className="text-[11px] text-muted-foreground text-center mt-2">
                Savings if Basic FSI only were used: <span className="font-semibold text-green-700">{formatCurrency(Math.max(0, costBreakdown.totalCostsNormalMode - costBreakdown.totalCostsBasicMode))}</span>
              </div>
            </div>

            <div className="flex justify-end">
              <Button variant="outline" size="sm" className="rounded-sm" onClick={exportFSICostsPDF} data-testid="export-fsi-costs-pdf">
                <Download className="mr-2 h-4 w-4" />
                Export PDF
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {!fsiBreakdown && (
        <Card className="rounded-sm shadow-sm bg-secondary/50">
          <CardContent className="py-8 text-center text-muted-foreground">
            <p>Please complete the FSI Calculation tab first</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default FSISanctionCosts;
